import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation, useQueries } from '@tanstack/react-query';
import { Bottles, Catalog, Receipts, type VinmonopoletProduct } from '@/lib/api';
import { Badge, Button, Card, EmptyState, Icon, Input, Spinner, Textarea } from '@/design-system';
import { BottleImage } from '@/components/BottleCard';
import { useToasts } from '@/lib/store';

interface Line {
  /** Stable id for React keys. */
  id: string;
  /** What the user sees and can edit. */
  query: string;
  /** Toggled per row. */
  selected: boolean;
  /** Which catalog candidate to use. null = manual (use raw text). */
  pickedProductId: string | null;
}

const NOISE_PATTERNS = [
  /^vinmonopolet/i,
  /^kvittering/i,
  /^kassakvittering/i,
  /^kassanr/i,
  /^kvitt(ering)?nr/i,
  /^dato\b/i,
  /^kl\b/i,
  /^total\b/i,
  /^sum\b/i,
  /^subtotal\b/i,
  /^mva\b/i,
  /^moms\b/i,
  /^kortkjøp/i,
  /^bankaxept/i,
  /^visa\b/i,
  /^mastercard\b/i,
  /^kontant/i,
  /^antall\b/i,
  /^pris\b/i,
  /^varenr\b/i,
  /^takk\b/i,
  /^velkommen/i,
  /^åpningstid/i,
  /^org(\.|\.nr)/i,
  /^terminalid/i,
  /^autoris/i,
  /^referanse/i,
  /^\d{2}[.\-/]\d{2}[.\-/]\d{2,4}$/, // dates
  /^\d{1,2}[:.]\d{2}$/, // times
  /^[\s.\-_=*]+$/, // separators
  /^\s*$/,
];

/**
 * Parse a shop receipt into candidate product names.
 * Strips line numbers, prices, common header/footer noise, and tries to keep
 * just the wine/spirit name on each line.
 */
function parseReceipt(input: string): string[] {
  const lines = input
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  const candidates: string[] = [];
  for (const raw of lines) {
    if (NOISE_PATTERNS.some((rx) => rx.test(raw))) continue;

    // Strip leading qty markers ("1x ", "2 stk ", "*"), trailing price ("kr 199,00", "199.00", "199,-").
    const stripped = raw
      .replace(/^[*•\-]+\s*/, '')
      .replace(/^\d+\s*(?:x|stk|stk\.|pcs)\s+/i, '')
      .replace(/\s+(?:kr\.?\s*)?\d{1,5}[.,]?\d{0,2}\s*(?:kr|,-|nok)?\s*$/i, '')
      .trim();

    // Skip remnants that are just numbers, single chars, or very short.
    if (stripped.length < 3) continue;
    if (/^\d+$/.test(stripped)) continue;

    candidates.push(stripped);
  }

  return candidates;
}

export function ReceiptBulkPage() {
  const navigate = useNavigate();
  const { push } = useToasts();
  const [text, setText] = useState('');
  const [lines, setLines] = useState<Line[]>([]);
  const [submitted, setSubmitted] = useState(false);

  const handleParse = () => {
    const parsed = parseReceipt(text);
    setLines(
      parsed.map((q, idx) => ({
        id: `line-${idx}-${q.slice(0, 8)}`,
        query: q,
        selected: true,
        pickedProductId: null, // resolved to first candidate once search returns
      })),
    );
    setSubmitted(false);
  };

  // Upload a receipt photo → server-side Tesseract OCR → fill text + auto-parse.
  const ocrM = useMutation({
    mutationFn: async (file: File) => {
      const dataUrl = await readFileAsDataUrl(file);
      const base64 = dataUrl.split(',', 2)[1] ?? '';
      return Receipts.ocr({ imageBase64: base64, mimeType: file.type || undefined });
    },
    onSuccess: (result) => {
      setText(result.text);
      const parsed = parseReceipt(result.text);
      setLines(
        parsed.map((q, idx) => ({
          id: `line-${idx}-${q.slice(0, 8)}`,
          query: q,
          selected: true,
          pickedProductId: null,
        })),
      );
      setSubmitted(false);
      push({
        title: parsed.length > 0
          ? `Fant ${parsed.length} ${parsed.length === 1 ? 'linje' : 'linjer'}`
          : 'Ingen produkter funnet',
        description: `Tesseract OCR · ${Math.round(result.confidence)}% sikkerhet`,
        tone: parsed.length > 0 ? 'success' : 'warning',
      });
    },
    onError: (err: Error) =>
      push({ title: 'OCR feilet', description: err.message, tone: 'danger' }),
  });

  const updateLine = (id: string, patch: Partial<Line>) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  };

  const removeLine = (id: string) => {
    setLines((prev) => prev.filter((l) => l.id !== id));
  };

  // Run a Catalog.search per active line — return up to 5 candidates so the
  // user can pick the right vintage / cork colour / format variant.
  const matchQueries = useQueries({
    queries: lines.map((line) => ({
      queryKey: ['receipt.match', line.query],
      queryFn: () => Catalog.search(line.query, 5),
      enabled: line.query.trim().length >= 3,
      staleTime: 5 * 60_000,
    })),
  });

  const lineCandidates = useMemo(() => {
    const map = new Map<string, VinmonopoletProduct[]>();
    lines.forEach((line, idx) => {
      map.set(line.id, matchQueries[idx]?.data?.products ?? []);
    });
    return map;
  }, [lines, matchQueries]);

  // Auto-pick the top match once a search resolves and the user hasn't
  // explicitly chosen a candidate yet. If multiple candidates exist we leave
  // pickedProductId null so the user is forced to choose visibly.
  useEffect(() => {
    setLines((prev) =>
      prev.map((line, idx) => {
        if (line.pickedProductId !== null) return line;
        const candidates = matchQueries[idx]?.data?.products ?? [];
        if (candidates.length === 1) {
          return { ...line, pickedProductId: candidates[0].basic.productId };
        }
        return line;
      }),
    );
  }, [matchQueries.map((q) => q.data?.products?.length ?? 0).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = useMutation({
    mutationFn: async () => {
      const targets = lines.filter((l) => l.selected);
      const results = await Promise.allSettled(
        targets.map(async (line) => {
          const candidates = lineCandidates.get(line.id) ?? [];
          const picked = candidates.find((c) => c.basic.productId === line.pickedProductId);
          if (picked) {
            const country = (picked.origins as { country?: string } | undefined)?.country;
            const region = (picked.origins as { region?: string } | undefined)?.region;
            return Bottles.create({
              productId: picked.basic.productId,
              catalogId: picked.basic.productId,
              name: picked.basic.productLongName ?? picked.basic.productShortName ?? line.query,
              vintage: picked.basic.vintage,
              country,
              region,
              photoUrl: picked.imageUrl,
              qty: 1,
            });
          }
          // No pick (none chosen, or no candidates returned) — create manual.
          return Bottles.create({ name: line.query, qty: 1 });
        }),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      return { ok, failed };
    },
    onSuccess: ({ ok, failed }) => {
      setSubmitted(true);
      push({
        title: `${ok} ${ok === 1 ? 'flaske' : 'flasker'} lagt til`,
        description: failed > 0 ? `${failed} feilet — sjekk loggen.` : undefined,
        tone: failed > 0 ? 'warning' : 'success',
      });
      if (failed === 0) {
        setTimeout(() => navigate('/cellar'), 600);
      }
    },
    onError: (err: Error) =>
      push({ title: 'Kunne ikke legge til', description: err.message, tone: 'danger' }),
  });

  // Reset submitted flag when user changes lines after submit
  useEffect(() => {
    if (submitted) setSubmitted(false);
  }, [lines]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = lines.filter((l) => l.selected).length;
  const allSelected = lines.length > 0 && selectedCount === lines.length;
  const needsPickCount = lines.filter((l, idx) => {
    if (!l.selected) return false;
    const candidates = matchQueries[idx]?.data?.products ?? [];
    return candidates.length > 1 && l.pickedProductId === null;
  }).length;

  return (
    <div className="grid gap-6 max-w-3xl">
      <header className="grid gap-2">
        <Link to="/scan" className="text-xs uppercase tracking-widest text-mist hover:text-candle">
          ← Tilbake til skann
        </Link>
        <h1 className="ui-section-title text-3xl">Bulk fra kvittering</h1>
        <p className="text-sm text-mist">
          Lim inn teksten fra en butikkvittering — vi finner flaskene og legger dem
          i kjelleren i én operasjon.
        </p>
      </header>

      {lines.length === 0 ? (
        <Card className="grid gap-4 p-5">
          <div className="flex items-center gap-3">
            <Icon name="receipt" className="text-candle" />
            <h2 className="font-serif text-lg text-parchment">Last opp eller lim inn</h2>
          </div>

          <ReceiptPhotoUpload
            onPick={(file) => ocrM.mutate(file)}
            loading={ocrM.isPending}
          />

          <div className="relative grid place-items-center py-1">
            <span className="absolute inset-x-0 top-1/2 h-px bg-line" />
            <span className="relative bg-paper px-3 text-[10px] uppercase tracking-widest text-mist">
              eller lim inn tekst
            </span>
          </div>

          <Textarea
            rows={10}
            placeholder={`Eksempel:\nButikk AS\nDato 26.04.2026\n1x Pecchenino San Giuseppe Barolo  399,00\n1x La Vieille Ferme Rouge          129,00\nTotal                              528,00`}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[11px] uppercase tracking-widest text-mist">
              Vi prøver å filtrere bort overskrifter, datoer, totaler og priser.
            </span>
            <Button
              variant="primary"
              iconLeft="sparkles"
              disabled={text.trim().length < 5}
              onClick={handleParse}
            >
              Tolk kvittering
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="grid gap-3 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="font-serif text-lg text-parchment">
                {lines.length} {lines.length === 1 ? 'linje' : 'linjer'} funnet
              </h2>
              <p className="text-xs text-mist">
                Avhuk det du vil legge til. Endre navn for bedre treff. Ingen treff blir lagt til
                som manuelle flasker.
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  setLines((prev) => prev.map((l) => ({ ...l, selected: !allSelected })))
                }
              >
                {allSelected ? 'Avhuk alle' : 'Velg alle'}
              </Button>
              <Button size="sm" variant="ghost" iconLeft="x" onClick={() => setLines([])}>
                Start på nytt
              </Button>
            </div>
          </div>

          <ul className="grid gap-3">
            {lines.map((line, idx) => {
              const status = matchQueries[idx];
              const candidates = lineCandidates.get(line.id) ?? [];
              const needsPick = candidates.length > 1 && line.pickedProductId === null;
              return (
                <li
                  key={line.id}
                  className={`grid gap-3 rounded-md border p-3 transition-colors ${
                    !line.selected
                      ? 'border-line opacity-60'
                      : needsPick
                        ? 'border-[color:var(--ui-color-danger)]/50 bg-ink-soft'
                        : line.pickedProductId
                          ? 'border-candle/50 bg-candle/5'
                          : 'border-line'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      aria-label={line.selected ? 'Avmerk' : 'Velg'}
                      onClick={() => updateLine(line.id, { selected: !line.selected })}
                      className={`mt-1 grid h-6 w-6 shrink-0 place-items-center rounded-md border ${
                        line.selected
                          ? 'border-candle bg-candle text-on-primary'
                          : 'border-line-soft text-mist hover:border-candle'
                      }`}
                    >
                      {line.selected ? <Icon name="check" size={14} /> : null}
                    </button>
                    <Input
                      value={line.query}
                      onChange={(e) =>
                        updateLine(line.id, { query: e.target.value, pickedProductId: null })
                      }
                      className="flex-1"
                    />
                    <button
                      type="button"
                      onClick={() => removeLine(line.id)}
                      className="text-mist hover:text-[color:var(--ui-color-danger)]"
                      aria-label="Fjern linje"
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>

                  {/* Candidate picker — horizontal scroll for ≥2 matches */}
                  <div className="pl-9">
                    {status.isLoading ? (
                      <span className="text-xs text-mist">Søker katalog...</span>
                    ) : status.isError ? (
                      <span className="text-xs text-[color:var(--ui-color-danger)]">
                        Kunne ikke søke
                      </span>
                    ) : candidates.length === 0 ? (
                      <span className="text-xs text-mist">
                        Ingen treff — opprettes som manuell flaske
                      </span>
                    ) : (
                      <>
                        {needsPick && (
                          <p className="mb-2 text-xs text-[color:var(--ui-color-danger)]">
                            <Icon name="warning" size={11} className="mr-1 inline-block" />
                            Velg riktig variant — flere kandidater funnet
                          </p>
                        )}
                        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                          {candidates.map((c) => {
                            const picked = line.pickedProductId === c.basic.productId;
                            return (
                              <button
                                key={c.basic.productId}
                                type="button"
                                onClick={() =>
                                  updateLine(line.id, {
                                    pickedProductId: picked ? null : c.basic.productId,
                                  })
                                }
                                className={`relative grid w-28 shrink-0 gap-1 rounded-md border p-2 text-left transition-colors sm:w-32 ${
                                  picked
                                    ? 'border-candle bg-candle/10'
                                    : 'border-line hover:border-line-soft'
                                }`}
                              >
                                <div className="relative grid h-16 place-items-center overflow-hidden rounded bg-ink-soft sm:h-20">
                                  <BottleImage
                                    src={c.imageUrl}
                                    iconSize={20}
                                    withBackdrop
                                  />
                                  {picked && (
                                    <span className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-candle text-on-primary">
                                      <Icon name="check" size={11} />
                                    </span>
                                  )}
                                </div>
                                <span className="line-clamp-2 font-serif text-xs leading-tight text-parchment">
                                  {c.basic.productLongName ?? c.basic.productShortName}
                                </span>
                                <span className="text-[9px] uppercase tracking-widest text-mist">
                                  #{c.basic.productId}
                                  {c.basic.vintage ? ` · ${c.basic.vintage}` : ''}
                                </span>
                              </button>
                            );
                          })}
                          {/* "Manual" option — always available */}
                          <button
                            type="button"
                            onClick={() => updateLine(line.id, { pickedProductId: null })}
                            className={`grid w-28 shrink-0 place-items-center gap-1 rounded-md border-2 border-dashed p-2 text-center transition-colors sm:w-32 ${
                              line.pickedProductId === null
                                ? 'border-mist bg-ink-soft'
                                : 'border-line hover:border-line-soft'
                            }`}
                          >
                            <Icon name="bottle-empty" size={20} className="text-mist/60" />
                            <span className="text-[10px] uppercase tracking-widest text-mist">
                              Manuell
                            </span>
                            <span className="text-[9px] text-mist/70">
                              ingen katalogkobling
                            </span>
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          {selectedCount === 0 ? (
            <EmptyState icon="bottle-empty" title="Ingen valgt" description="Velg minst én linje for å fortsette." />
          ) : (
            <div className="sticky bottom-2 grid gap-2 rounded-md border border-candle/40 bg-ink/95 p-3 backdrop-blur-sm sm:flex sm:items-center sm:justify-between">
              <span className="text-sm text-parchment">
                Klar til å legge til <strong>{selectedCount}</strong>{' '}
                {selectedCount === 1 ? 'flaske' : 'flasker'}
                {needsPickCount > 0 && (
                  <span className="ml-2 text-xs text-[color:var(--ui-color-danger)]">
                    · {needsPickCount} mangler valg
                  </span>
                )}
              </span>
              <Button
                variant="primary"
                iconLeft="add-bottle"
                disabled={needsPickCount > 0}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                Legg i kjelleren
              </Button>
            </div>
          )}

          {submitted && create.isError ? (
            <p className="text-xs text-[color:var(--ui-color-danger)]">
              Noe feilet — sjekk varsler.
            </p>
          ) : null}
        </Card>
      )}
    </div>
  );
}

function ReceiptPhotoUpload({
  onPick,
  loading,
}: {
  onPick: (file: File) => void;
  loading: boolean;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  return (
    <div className="grid gap-3">
      <label
        className={`grid cursor-pointer place-items-center gap-2 rounded-md border border-dashed border-line p-6 text-center transition hover:border-candle ${
          loading ? 'pointer-events-none opacity-60' : ''
        }`}
      >
        {preview ? (
          <img src={preview} alt="Forhåndsvisning" className="max-h-48 rounded-md" />
        ) : (
          <>
            <Icon name="camera" size={28} className="text-candle" />
            <span className="font-serif text-base text-parchment">Last opp eller ta bilde av kvitteringen</span>
            <span className="text-[11px] uppercase tracking-widest text-mist">
              JPG · PNG · HEIC · webp · opp til 10 MB
            </span>
          </>
        )}
        <input
          type="file"
          accept="image/*,.heic,.heif"
          capture="environment"
          className="hidden"
          disabled={loading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            readFileAsDataUrl(file).then(setPreview).catch(() => setPreview(null));
            onPick(file);
            e.target.value = '';
          }}
        />
      </label>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-mist">
          <Spinner size={14} /> Leser kvitteringen…
        </div>
      )}
    </div>
  );
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}
